import { useEffect, useState } from 'react';

import {
  Box,
  Flex,
  HStack,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Progress,
  SimpleGrid,
  Switch,
  Text
} from '@chakra-ui/react';
import download from 'downloadjs';
import html2canvas from 'html2canvas';
import { t } from 'i18next';
import { first } from 'lodash';
import { useQuery } from 'react-query';

import { ReactComponent as ChevronDownIcon } from '../../icons/chevron.down.small.svg';
import { ReactComponent as SaveIcon } from '../../icons/save.svg';
import { getArtistId, getMemberIdAnyway } from '../../utils/etcUtils';
import { setEvent } from '../../utils/ga';
import { queryGet } from '../../utils/queryClient';
import FilledButton from '../FilledButton';
import LikedMembersList from '../lists/LikedMembersList';
import PhotoCardsByEvent from '../PhotoCardsByEvent';

const BOARD_CONTENT_ID = 'my-photo-card-board-content';
const BOARD_ACTION_ID = 'my-photo-card-board-bottom-action';

const SURFACE_EVENT_MAP = {
  bottomSheet: 'Show MyPhotoCard BottomSheet',
  page: 'Show MyPhotoCard Page'
};

const collectedPhotoCards = (photoCards) => {
  return photoCards?.filter((photoCard) => photoCard.collectedCount > 0);
};

const unCollectedPhotoCards = (photoCards) => {
  return photoCards?.filter((photoCard) => photoCard.collectedCount === 0);
};

const wishedPhotoCards = (photoCards) => {
  return photoCards?.filter((photoCard) => photoCard.isWished);
};

export function useMyPhotoCardBoard({
  members,
  isActive = true,
  onClose,
  surface = 'bottomSheet'
}) {
  const artistId = getArtistId();

  const [selectedMember, setSelectedMember] = useState(null);
  const [photoCardByEvent, setPhotoCardByEvent] = useState();
  const [selectedPhotoCards, setSelectedPhotoCards] = useState([]);
  const [isSaveImage, setIsSaveImage] = useState(false);
  const [showEventName, setShowEventName] = useState(false);
  const [gridType, setGridType] = useState('md');
  const [tabIndex, setTabIndex] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [sourceDataUrl, setSourceDataUrl] = useState();

  useEffect(() => {
    if (!isActive) return;

    setEvent({
      category: 'PhotoCard',
      action: SURFACE_EVENT_MAP[surface] || SURFACE_EVENT_MAP.bottomSheet,
      label: surface
    });
  }, [isActive, surface]);

  const { data: artistPhotoCards } = useQuery(
    ['artistPhotoCards', artistId, getMemberIdAnyway(selectedMember)],
    () => queryGet(`/artists/${artistId}/members/${getMemberIdAnyway(selectedMember)}/photo_cards`),
    {
      enabled: !!selectedMember && isActive
    }
  );

  useEffect(() => {
    setSelectedMember(first(members) || null);
  }, [members]);

  useEffect(() => {
    if (!isActive) return;

    switch (tabIndex) {
      case 1: {
        const unCollected = {
          events: artistPhotoCards?.events?.map((event) => {
            return {
              event: event.event,
              photoCards: unCollectedPhotoCards(event.photoCards)
            };
          })
        };

        setEvent({
          category: 'PhotoCard',
          action: 'Show UnCollected PhotoCards',
          label: `MyPhotoCardBoard:${surface}`
        });
        setPhotoCardByEvent(unCollected);
        setSelectedPhotoCards(
          unCollected?.events?.flatMap((event) => unCollectedPhotoCards(event.photoCards))
        );
        return;
      }
      case 2: {
        const wished = {
          events: artistPhotoCards?.events?.map((event) => {
            return {
              event: event.event,
              photoCards: wishedPhotoCards(event.photoCards)
            };
          })
        };

        setEvent({
          category: 'PhotoCard',
          action: 'Show Wished PhotoCards',
          label: `MyPhotoCardBoard:${surface}`
        });
        setPhotoCardByEvent(wished);
        setSelectedPhotoCards(
          wished?.events?.flatMap((event) => wishedPhotoCards(event.photoCards))
        );
        return;
      }
      default: {
        const collected = {
          events: artistPhotoCards?.events?.map((event) => {
            return {
              event: event.event,
              photoCards: collectedPhotoCards(event.photoCards)
            };
          })
        };

        setEvent({
          category: 'PhotoCard',
          action: 'Show Collected PhotoCards',
          label: `MyPhotoCardBoard:${surface}`
        });
        setPhotoCardByEvent(collected);
        setSelectedPhotoCards(
          collected?.events?.flatMap((event) => collectedPhotoCards(event.photoCards))
        );
      }
    }
  }, [artistPhotoCards, isActive, surface, tabIndex]);

  const onChangedMember = (member) => {
    setSelectedMember(member);
  };

  const getPhotoSizeText = () => {
    switch (gridType) {
      case 'sm':
        return t('myPhotoCardBottomSheet.smallSize');
      case 'md':
        return t('myPhotoCardBottomSheet.middleSize');
      case 'lg':
        return t('myPhotoCardBottomSheet.largeSize');
      default:
        return t('myPhotoCardBottomSheet.largeSize');
    }
  };

  const toggleSwitchChanged = () => {
    setShowEventName((prevShowEventName) => !prevShowEventName);
  };

  const convertSourceToCanvas = () => {
    setEvent({
      category: 'PhotoCard',
      action: 'Create PhotoCard Board',
      label: surface
    });

    setIsExporting(true);

    const sourceElement = document.getElementById('source');
    html2canvas(sourceElement, {
      backgroundColor: null,
      allowTaint: false,
      useCORS: true,
      imageTimeout: 0,
      onclone: (cloned) => {
        cloned.getElementById('source').style.display = 'block';
      }
    }).then((canvas) => {
      setIsExporting(false);
      setIsSaveImage(true);
      setSourceDataUrl(canvas.toDataURL('image/png'));
    });
  };

  const capturePhotoCardBoard = () => {
    setEvent({
      category: 'PhotoCard',
      action: 'Save PhotoCard Board',
      label: surface
    });
    download(sourceDataUrl, `pocapan_${Date.now()}.png`);
  };

  const sharePhotoCardBoard = () => {
    setEvent({
      category: 'PhotoCard',
      action: 'Share PhotoCard Board',
      label: surface
    });
    window.location.href = t('myPhotoCardBottomSheet.shareTwiiterText');
  };

  const isScrollableState = () => {
    const contentElement = document.getElementById(BOARD_CONTENT_ID);
    const actionElement = document.getElementById(BOARD_ACTION_ID);

    return (
      contentElement?.scrollHeight > window.innerHeight ||
      selectedPhotoCards?.length > (getPhotoSizeText() === 'md' ? 8 : 20) ||
      actionElement?.getBoundingClientRect()?.y > window.innerHeight
    );
  };

  const exitSaveImage = () => {
    setIsSaveImage(false);
    setIsExporting(false);
    setSourceDataUrl(null);
  };

  const closeBoard = () => {
    exitSaveImage();
    onClose?.();
  };

  return {
    isExporting,
    isSaveImage,
    photoCardByEvent,
    selectedPhotoCards,
    showEventName,
    gridType,
    sourceDataUrl,
    tabIndex,
    capturePhotoCardBoard,
    closeBoard,
    convertSourceToCanvas,
    exitSaveImage,
    getPhotoSizeText,
    isScrollableState,
    onChangedMember,
    setGridType,
    setTabIndex,
    sharePhotoCardBoard,
    toggleSwitchChanged
  };
}

export default function MyPhotoCardBoardContent({
  board,
  bottomButtonClassName = 'bottomButton',
  bottomButtonStyle
}) {
  const isScrollable = board.isScrollableState();

  return (
    <div id={BOARD_CONTENT_ID}>
      {board.isSaveImage
        ? <>
            <Image src={board.sourceDataUrl} />
            <HStack mt={10}>
              <Box
                className="bg-primary-light dark:bg-primary-dark"
                padding={'10px'}
                borderRadius={'full'}
                onClick={() => board.capturePhotoCardBoard()}
              >
                <div className="px-[6px] py-[6px]">
                  <SaveIcon className="fill-white" width={'24px'} height={'24px'} />
                </div>
              </Box>
              <FilledButton
                width={'full'}
                borderRadius={'32px'}
                paddingX={'16px'}
                paddingY={'16px'}
                fontSize={'17px'}
                fontWeight={600}
                lineHeight={'20px'}
                text={t('myPhotoCardBottomSheet.shareImageTwitterButton')}
                filled={true}
                onClicked={() => board.sharePhotoCardBoard()}
              />
            </HStack>
          </>
        : <>
            <HStack paddingY={'10px'}>
              <Text
                className={
                  board.tabIndex === 0
                    ? 'text-pure-black dark:text-pure-white'
                    : 'text-onDisabled-light dark:text-onDisabled-dark'
                }
                fontWeight={600}
                fontSize={'20px'}
                lineHeight={'24px'}
                as={board.tabIndex === 0 ? 'b' : undefined}
                onClick={() => board.setTabIndex(0)}
              >
                {t('myPhotoCardBottomSheet.collectedPhotoCards')}
              </Text>
              <Text
                className={
                  board.tabIndex === 1
                    ? 'text-pure-black dark:text-pure-white'
                    : 'text-onDisabled-light dark:text-onDisabled-dark'
                }
                fontWeight={600}
                fontSize={'20px'}
                lineHeight={'24px'}
                as={board.tabIndex === 1 ? 'b' : undefined}
                onClick={() => board.setTabIndex(1)}
              >
                {t('myPhotoCardBottomSheet.unCollectedPhotoCards')}
              </Text>
              <Text
                className={
                  board.tabIndex === 2
                    ? 'text-pure-black dark:text-pure-white'
                    : 'text-onDisabled-light dark:text-onDisabled-dark'
                }
                fontWeight={600}
                fontSize={'20px'}
                lineHeight={'24px'}
                as={board.tabIndex === 2 ? 'b' : undefined}
                onClick={() => board.setTabIndex(2)}
              >
                {t('myPhotoCardBottomSheet.wishedPhotoCards')}
              </Text>
            </HStack>
            <LikedMembersList onChangeLikedMember={board.onChangedMember} />
            <SimpleGrid columns={2} alignItems="center" paddingY={'14px'}>
              <Menu>
                <MenuButton
                  size="xs"
                  className="bg-background-light dark:bg-backgroundElevated-dark"
                >
                  <HStack spacing={0}>
                    <Text
                      className="text-main-light-3 dark:text-main-dark-3"
                      fontSize={'13px'}
                      fontWeight={400}
                      lineHeight={'16px'}
                      marginRight={'4px'}
                    >
                      {board.getPhotoSizeText()}
                    </Text>
                    <ChevronDownIcon
                      className="fill-main-light-3 dark:fill-main-dark-3"
                      width={'20px'}
                      height={'20px'}
                    />
                  </HStack>
                </MenuButton>
                <MenuList>
                  <MenuItem key={'sm'} onClick={() => board.setGridType('sm')}>
                    {t('myPhotoCardBottomSheet.smallSize')}
                  </MenuItem>
                  <MenuItem key={'md'} onClick={() => board.setGridType('md')}>
                    {t('myPhotoCardBottomSheet.middleSize')}
                  </MenuItem>
                  <MenuItem key={'lg'} onClick={() => board.setGridType('lg')}>
                    {t('myPhotoCardBottomSheet.largeSize')}
                  </MenuItem>
                </MenuList>
              </Menu>
              <div className="flex justify-end items-center">
                <Switch
                  size="sm"
                  colorScheme="primary"
                  isChecked={board.showEventName}
                  onChange={() => board.toggleSwitchChanged()}
                />
                <Text
                  className="text-main-light-3 dark:text-main-dark-3"
                  fontSize={'13px'}
                  fontWeight={400}
                  lineHeight={'16px'}
                  paddingLeft={2}
                >
                  {t('myPhotoCardBottomSheet.showPhotoCardName')}
                </Text>
              </div>
            </SimpleGrid>
            <Flex
              height={isScrollable ? undefined : 'calc(100% - 150px)'}
              direction={'column'}
              justifyContent={'space-between'}
              marginBottom={'80px'}
            >
              <div
                id="source"
                style={{
                  display: 'none',
                  borderRadius: '18px',
                  padding: '12px',
                  background: "url('/assets/backgrounds/holography.png')",
                  backgroundSize: 'cover'
                }}
              >
                <div
                  style={{
                    paddingTop: '4px',
                    paddingBottom: '12px',
                    paddingLeft: '10px',
                    paddingRight: '10px',
                    borderRadius: '8px',
                    background:
                      'linear-gradient(90deg, rgba(255, 255, 255, 0.3) 100%, rgba(255, 255, 255, 0.1) 100%), linear-gradient(134.41deg, rgba(255, 255, 255, 0.4) 10%, rgba(255, 255, 255, 0) 100%)'
                  }}
                >
                  <PhotoCardsByEvent
                    photoCardsByEvent={board.photoCardByEvent}
                    selectedPhotoCards={board.selectedPhotoCards}
                    collectingMode={false}
                    showEventName={board.showEventName}
                    gridType={board.gridType}
                    onClickedPhotoCard={() => {}}
                  />
                  <div className="w-full flex justify-center">
                    <div className="w-3/4 mt-10 mb-5">
                      <img alt="pocapan logo" src="/assets/logos/pocapan.png" />
                    </div>
                  </div>
                </div>
              </div>
              <PhotoCardsByEvent
                photoCardsByEvent={board.photoCardByEvent}
                selectedPhotoCards={board.selectedPhotoCards}
                collectingMode={false}
                showEventName={board.showEventName}
                gridType={board.gridType}
                onClickedPhotoCard={() => {}}
              />
              <div id={BOARD_ACTION_ID} className={bottomButtonClassName} style={bottomButtonStyle}>
                {board.isExporting
                  ? <Progress
                      size="sm"
                      colorScheme="primary"
                      isIndeterminate
                      borderRadius={'12px'}
                    />
                  : board.selectedPhotoCards?.length > 0 && (
                      <FilledButton
                        paddingX={'16px'}
                        paddingY={'16px'}
                        fontSize={'17px'}
                        fontWeight={600}
                        lineHeight={'20px'}
                        borderRadius={'32px'}
                        marginTop={isScrollable ? '20px' : undefined}
                        text={t('myPhotoCardBottomSheet.createPhotoCardBoard')}
                        filled={true}
                        onClicked={() => board.convertSourceToCanvas()}
                      />
                    )}
              </div>
            </Flex>
          </>}
    </div>
  );
}
